import React, { FC, useMemo } from 'react'
import { FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { makeStyles } from '@mui/styles'
import { SelectArrow } from '../SelectArrow'
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter'
import { Colors } from '../../theme'

const useStyles = makeStyles({
  inputContainer: {
    width: ({ width, fullWidth }: InputSelectProps) => (fullWidth ? '100%' : width),
    height: ({ height }: InputSelectProps) => height || 63,
    marginTop: ({ mt }: InputSelectProps) => mt,
    marginBottom: ({ mb }: InputSelectProps) => mb,
    marginLeft: ({ ml }: InputSelectProps) => ml,
    marginRight: ({ mr }: InputSelectProps) => mr,
    paddingLeft: ({ pl }: InputSelectProps) => pl,
    // height: 63,
    '&.placeHolder .MuiSelect-select': {
      color: 'rgba(255, 255, 255, 0.6)',
    },
    '& .MuiSelect-select': {
      fontSize: 16,
      padding: ({ padding }: InputSelectProps) => padding,
    },
    '& .Mui-disabled': {
      '& .MuiSelect-select': {
        color: Colors.white,
        opacity: '30%',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.4)',
      },
    },
  },
  label: {
    fontSize: '18px',
    marginBottom: '-3px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  formControl: {
    width: ({ width, fullWidth }: InputSelectProps) => (fullWidth ? '100%' : width),
    height: '40px',
    color: '#FFFFFF',
    boxSizing: 'border-box',
    '& .MuiOutlinedInput-root.Mui-disabled': {
      '& fieldset': {
        borderColor: '#FFFFFF',
        opacity: 0.4,
      },
    },
  },
  selectField: {
    color: Colors.white,
    '&.Mui-disabled': {
      '& .MuiSelect-select': {
        opacity: 1,
        color: Colors.white,
        WebkitTextFillColor: `${Colors.white} !important`,
      },
      '& .MuiSelect-icon': {
        color: '#FFFFFF',
      },
    },
  },
  placeHolder: {
    '& .MuiSelect-select': {
      color: 'rgba(255, 255, 255, 0.6)',
    },
  },
})

export interface IEnums {
  value: string
  label: string
}

interface InputSelectProps {
  enums?: IEnums[]
  labelName?: string
  inputName: string
  placeholder?: string
  width?: number | string
  height?: number | string
  mt?: number | string
  mb?: number | string
  ml?: number | string
  mr?: number | string
  pl?: number | string
  padding?: number | string
  disable?: boolean | undefined
  enumDisableValue?: string
  defaultValue?: string
  onBlurSelect?: ($event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  fullWidth?: boolean
}

const selectArr = [
  { id: 1, city: 'Num 1' },
  { id: 2, city: 'Num 2' },
  { id: 3, city: 'Num 3' },
]

export const InputSelect: FC<InputSelectProps> = ({
  enums,
  labelName,
  inputName,
  placeholder,
  width,
  height,
  pl,
  mb,
  ml,
  mr,
  mt,
  padding,
  disable,
  enumDisableValue,
  defaultValue = '',
  onBlurSelect,
  fullWidth,
}) => {
  const styles = useStyles({ width, fullWidth, height, mt, mb, pl, placeholder, inputName, ml, mr, padding })
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext()
  const error = errors[inputName]

  const errorMessage = useMemo(() => {
    const message = error?.message.replace(inputName, '')
    return capitalizeFirstLetter(message)
  }, [error?.message, inputName])

  return (
    <Grid container className={styles.inputContainer} direction={'column'} wrap={'nowrap'}>
      {labelName && (
        <Grid item>
          <InputLabel shrink htmlFor={inputName} className={styles.label}>
            {labelName}
          </InputLabel>
        </Grid>
      )}

      <Grid item>
        <FormControl className={styles.formControl} variant={'outlined'} size={'small'}>
          <Controller
            rules={{ required: true }}
            control={control}
            name={inputName}
            defaultValue={defaultValue || ''}
            render={({ field: { onBlur, onChange, value, ref } }) => (
              <Select
                id={inputName}
                displayEmpty
                IconComponent={SelectArrow}
                disabled={disable}
                {...register(inputName)}
                error={!!error}
                className={value ? styles.selectField : styles.placeHolder}
                inputProps={{ 'aria-label': placeholder || 'Without label' }}
                value={value || ''}
                onChange={onChange}
                inputRef={ref}
                onBlur={($event) => {
                  onBlur()
                  if (onBlurSelect) {
                    onBlurSelect($event)
                  }
                }}
              >
                {placeholder && (
                  <MenuItem className={styles.placeHolder} value="" disabled>
                    {placeholder}
                  </MenuItem>
                )}

                {enums &&
                  Object.keys(enums).length !== 0 &&
                  enums.map((data: IEnums, index: number) => (
                    <MenuItem
                      key={index + data.value}
                      disabled={data.value === enumDisableValue}
                      value={data.value}
                      className={styles.selectField}
                    >
                      {data.label}
                    </MenuItem>
                  ))}

                {!enums &&
                  selectArr.map((el) => (
                    <MenuItem key={el.id} value={el.city} className={styles.selectField}>
                      {el.city}
                    </MenuItem>
                  ))}
              </Select>
            )}
          />
        </FormControl>
      </Grid>
      <Grid item>
        {error && (
          <FormHelperText error id="component-error-text">
            {errorMessage}
          </FormHelperText>
        )}
      </Grid>
    </Grid>
  )
}
